import React, { useEffect, useMemo, useState } from 'react';
import { Backdrop, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputAdornment, Snackbar, Tab, Tabs, TextField, Tooltip, Typography, Autocomplete } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import ClearIcon from '@mui/icons-material/Clear';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import trLocale from 'date-fns/locale/tr';
import axios from 'src/utils/axios';
import Alert from 'src/components/Alert';
import ExcelFileCreator from 'src/components/ExcelFileCreator';
import Page from 'src/components/Page';

const queryValue = (value, key) => `${key}=${encodeURIComponent(value ?? '')}`;

const styles = {
  filters: (theme) => ({ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2), marginBottom: theme.spacing(2), alignItems: 'flex-start' }),
  filterItem: { flex: '1 1 240px', minWidth: 240, maxWidth: 400 },
  searchField: (theme) => ({
    width: 240,
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: 20,
      backgroundColor: theme.palette.background.paper
    }
  }),
  actions: (theme) => ({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }),
  actionButtonsRow: (theme) => ({ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: theme.spacing(1.5) }),
  actionButton: (theme) => ({ borderRadius: 20, textTransform: 'none', fontWeight: 600, paddingLeft: theme.spacing(2.5), paddingRight: theme.spacing(2.5) }),
  excelButton: { backgroundColor: '#1D6F42', color: '#fff', '&:hover': { backgroundColor: '#155a34' } },
  searchButton: { backgroundColor: '#FF9800', color: '#fff', '&:hover': { backgroundColor: '#e68900' } },
  resultCount: (theme) => ({ marginBottom: theme.spacing(1) }),
  tableContainer: (theme) => ({ overflow: 'auto', maxHeight: 560, border: `1px solid ${theme.palette.divider}`, borderRadius: 8 }),
  table: { width: '100%', borderCollapse: 'collapse' },
  cell: (theme) => ({ padding: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.divider}`, whiteSpace: 'nowrap', fontSize: 13 }),
  row: (theme) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:hover': { backgroundColor: theme.palette.action.selected }
  }),
  header: (theme) => ({
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.grey[100],
    fontFamily: theme.typography.fontFamily || 'Roboto, Arial, sans-serif',
    fontSize: 13,
    fontWeight: 600,
    zIndex: 1
  }),
  backdrop: (theme) => ({ zIndex: theme.zIndex.drawer + 1, color: '#fff' }),
  multiSelectField: {
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '65px !important'
    },
    '& .MuiAutocomplete-endAdornment': {
      alignItems: 'center',
      display: 'flex',
      top: '50%',
      transform: 'translateY(-50%)'
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      padding: '4px'
    }
  },
  optionPopper: {
    '& .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: 'rgba(255, 152, 0, 0.12)'
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
      backgroundColor: 'rgba(255, 152, 0, 0.16)'
    },
    '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
      backgroundColor: 'rgba(255, 152, 0, 0.22)'
    }
  }
};

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedCheckboxIcon = <CheckBoxIcon fontSize="small" style={{ color: '#FF9800' }} />;
const ALL_OPTION = '__ALL__';

const MultiSelectAutocomplete = ({ sx, popperSx, label, options, value, onChange, getOptionLabel, getOptionKey, disabled }) => {
  const allSelected = options.length > 0 && value.length === options.length;
  const selectableOptions = options.length ? [ALL_OPTION, ...options] : [];

  const handleChange = (event, selected, reason, details) => {
    if (details?.option === ALL_OPTION) {
      onChange(allSelected ? [] : options);
      return;
    }
    onChange(selected.filter((item) => item !== ALL_OPTION));
  };

  const label_ = (option) => (option === ALL_OPTION ? 'Tümü' : getOptionLabel(option));

  return (
    <FormControl variant="outlined" fullWidth disabled={disabled}>
      <Autocomplete
        sx={sx}
        multiple
        size="small"
        fullWidth
        disabled={disabled}
        value={value}
        options={selectableOptions}
        componentsProps={{ popper: { sx: popperSx } }}
        getOptionLabel={label_}
        isOptionEqualToValue={(option, val) =>
          option === ALL_OPTION || val === ALL_OPTION ? option === val : getOptionKey(option) === getOptionKey(val)
        }
        onChange={handleChange}
        renderTags={(tagValue, getTagProps) => {
          if (allSelected) {
            return <Chip size="small" label={`Tümü (${tagValue.length})`} {...getTagProps({ index: 0 })} />;
          }
          const visibleCount = 1;
          return (
            <>
              {tagValue.slice(0, visibleCount).map((option, index) => (
                <Chip size="small" key={getOptionKey(option)} label={label_(option)} {...getTagProps({ index })} />
              ))}
              {tagValue.length > visibleCount && (
                <Chip size="small" variant="outlined" label={`+${tagValue.length - visibleCount}`} />
              )}
            </>
          );
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkedCheckboxIcon}
              style={{ marginRight: 8 }}
              checked={option === ALL_OPTION ? allSelected : selected}
            />
            {label_(option)}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label={label} variant="outlined" />}
      />
    </FormControl>
  );
};

const buildCsv = (selected, options, key) => (selected.length ? selected : options).map((item) => item[key]).join(',');

const REGIONS = [
  'Akdeniz',
  'Doğu Anadolu',
  'Ege',
  'Güneydoğu Anadolu',
  'İç Anadolu',
  'Karadeniz',
  'Marmara'
];
const COMPANIES = [
  { ID: 5, ACIKLAMA: 'ARÇELİK A.Ş.' },
  { ID: 7, ACIKLAMA: 'ARÇELİK LG' },
  { ID: 8, ACIKLAMA: 'AYGAZ A.Ş.' },
  { ID: 6, ACIKLAMA: 'RMK EAST MARINE DENİZCİLİK A.Ş.' }
];

const TseHybSurecleriView = () => {
  const [tab, setTab] = useState(0);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [region, setRegion] = useState([]);
  const [serviceSelection, setServiceSelection] = useState([]);
  const [standartOptions, setStandartOptions] = useState([]);
  const [standartSelection, setStandartSelection] = useState([]);
  const [companySelection, setCompanySelection] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    setServiceSelection([]);
    if (!region.length) {
      setServiceOptions([]);
      return;
    }

    axios.get('Servisler', {
      params: {
        bolge: region.join(',')
      }
    })
      .then((response) => {
        setServiceOptions(response.data || []);
      })
      .catch(() => {
        setServiceOptions([]);
      });
  }, [region]);

  useEffect(() => {
    axios.get('TseStandartlar')
      .then((response) => setStandartOptions(response.data || []))
      .catch(() => setStandartOptions([]));
  }, []);

  const loadManagement = () => {
    if (!region.length) {
      setNotice({ type: 'error', message: 'Lütfen bölge seçiniz.' });
      return;
    }
    setLoading(true);
    const birimKodu = serviceSelection.map((item) => item.BirimKodu).join(',');
    axios.get(`GetServiceBeratConfirm?${queryValue(region.join(','), 'BolgeKodu')}&${queryValue(birimKodu, 'BirimKodu')}`)
      .then((response) => setRows(response.data || []))
      .catch(() => setNotice({ type: 'error', message: 'Berat kayıtları alınamadı.' }))
      .finally(() => setLoading(false));
  };

  const loadReport = () => {
    if (!region.length) {
      setNotice({ type: 'error', message: 'Lütfen bölge seçiniz.' });
      return;
    }
    setLoading(true);
    const birimKodu = serviceSelection.map((item) => item.BirimKodu).join(',');
    axios.get(`GetApolloServiceBeratReport?${queryValue(region.join(','), 'BolgeKodu')}&${queryValue(birimKodu, 'BirimKodu')}`)
      .then((response) => setReportRows(response.data || []))
      .catch(() => setNotice({ type: 'error', message: 'Berat raporu alınamadı.' }))
      .finally(() => setLoading(false));
  };

  const confirm = (operationType) => {
    if (operationType === 2 && !comment.trim()) {
      setNotice({ type: 'error', message: 'Red işleminde yorum zorunludur.' });
      return;
    }
    setLoading(true);
    axios.post('SetServiceBeratConfirm', {
      Year: selectedRow.BeratYili,
      KulBirimKodu: selectedRow.ServisKodu,
      OperationType: operationType,
      Comment: comment
    })
      .then((response) => {
        const result = response.data;
        setNotice({ type: result.ErrorCode === '0' ? 'success' : 'error', message: result.ErrorDescription });
        if (result.ErrorCode === '0') loadManagement();
      })
      .catch(() => setNotice({ type: 'error', message: 'İşlem tamamlanamadı.' }))
      .finally(() => {
        setLoading(false);
        setSelectedRow(null);
        setComment('');
      });
  };

  const activeRows = tab === 0 ? rows : reportRows;
  const filteredRows = useMemo(() => activeRows.filter((row) =>
    `${row.ServisKodu || ''} ${row.ServisAdi || ''} ${row.ServisUnvani || ''}`.toLocaleLowerCase('tr')
      .includes(search.toLocaleLowerCase('tr'))
  ), [activeRows, search]);

  const exportRows = () => ExcelFileCreator(activeRows, tab === 0 ? 'Standart Uygunluk Sorgulama' : 'Standart Uygunluk Raporu');
  const searchRows = () => (tab === 0 ? loadManagement() : loadReport());

  return (
    <Page title="TSE HYB Süreçleri">
      <Backdrop sx={styles.backdrop} open={loading}><CircularProgress color="inherit" /></Backdrop>
      <Container maxWidth={false}>
        <Tabs value={tab} onChange={(event, value) => setTab(value)} indicatorColor="primary" textColor="primary" centered>
          <Tab label="Standart Uygunluk Sorgulama" />
          <Tab label="Standart Uygunluk Raporu" />
        </Tabs>
        <Card>
          <CardHeader title={tab === 0 ? 'Standart Uygunluk Sorgulama' : 'Standart Uygunluk Raporu'} />
          <CardContent>
            <Box sx={styles.filters}>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="Bölge"
                  options={REGIONS}
                  value={region}
                  onChange={setRegion}
                  getOptionLabel={(option) => option}
                  getOptionKey={(option) => option}
                />
              </Box>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="Servis"
                  options={serviceOptions}
                  value={serviceSelection}
                  onChange={setServiceSelection}
                  getOptionLabel={(option) => option.Dsc}
                  getOptionKey={(option) => option.BirimKodu}
                  disabled={!region.length}
                />
              </Box>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="TSE Standartlar"
                  options={standartOptions}
                  value={standartSelection}
                  onChange={setStandartSelection}
                  getOptionLabel={(option) => `${option.Standart} - ${option.StandartAciklama}`}
                  getOptionKey={(option) => option.Standart}
                />
              </Box>
              <Box style={styles.filterItem}>
                <MultiSelectAutocomplete
                  sx={styles.multiSelectField}
                  popperSx={styles.optionPopper}
                  label="Şirket"
                  options={COMPANIES}
                  value={companySelection}
                  onChange={setCompanySelection}
                  getOptionLabel={(option) => `${option.ID} - ${option.ACIKLAMA}`}
                  getOptionKey={(option) => option.ID}
                />
              </Box>
              <Box style={styles.filterItem}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
                  <DatePicker
                    label="Tarih"
                    format="dd.MM.yyyy"
                    value={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'outlined'
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
            <Box sx={styles.actions}>
              <Box sx={styles.actionButtonsRow}>
                <Tooltip title="Görüntülenen kayıtları Excel dosyası olarak indirir">
                  <span>
                    <Button
                      sx={[styles.actionButton, styles.excelButton]}
                      style={{ color: '#fff' }}
                      variant="contained"
                      onClick={exportRows}
                      startIcon={<GetAppIcon />}
                      disabled={!activeRows.length}
                    >
                      Excel İndir
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Seçili filtrelerle arama yapar">
                  <span>
                    <Button
                      sx={[styles.actionButton, styles.searchButton]}
                      style={{ color: '#fff' }}
                      variant="contained"
                      onClick={searchRows}
                      startIcon={<SearchIcon />}
                      disabled={loading}
                    >
                      Ara
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <TextField
                sx={styles.searchField}
                variant="outlined"
                size="small"
                label="Arama"
                placeholder="Servis kodu, adı veya unvanı..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Aramayı temizle" onClick={() => setSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            <Typography sx={styles.resultCount} variant="body2" color="textSecondary">
              {filteredRows.length} kayıt bulundu
            </Typography>
            <Box sx={styles.tableContainer}>
              <table style={styles.table}>
                <thead><tr>
                  {['Bölge Yöneticiliği Adı', 'Servis Kodu', 'Servis Adı', 'Servis Unvanı', 'Standart Numarası', 'Standart Adı', 'Belge Son Geçerlilik Tarihi', 'İl'].map((title) => <Box component="th" sx={[styles.cell, styles.header]} key={title}>{title}</Box>)}
                </tr></thead>
                <tbody>{filteredRows.map((row, index) => <Box component="tr" sx={styles.row} key={`${row.ServisKodu}-${index}`}>
                  <Box component="td" sx={styles.cell}>{row.BolgeYoneticiligi}</Box><Box component="td" sx={styles.cell}>{row.ServisKodu}</Box><Box component="td" sx={styles.cell}>{row.ServisAdi}</Box><Box component="td" sx={styles.cell}>{row.ServisUnvani}</Box>
                  <Box component="td" sx={styles.cell}>{row.StandartNumarasi}</Box><Box component="td" sx={styles.cell}>{row.StandartAdi}</Box><Box component="td" sx={styles.cell}>{row.BelgeSonGecerlilikTarihi}</Box><Box component="td" sx={styles.cell}>{row.Il}</Box>
                </Box>)}</tbody>
              </table>
              {!filteredRows.length && <Typography align="center" color="textSecondary" style={{ padding: 24 }}>Gösterilecek kayıt bulunamadı.</Typography>}
            </Box>
          </CardContent>
        </Card>
      </Container>
      <Dialog open={Boolean(selectedRow)} onClose={() => setSelectedRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>Berat Onay İşlemi</DialogTitle>
        <DialogContent><TextField autoFocus fullWidth multiline rows={4} variant="outlined" label="Yorum" value={comment} onChange={(event) => setComment(event.target.value)} /></DialogContent>
        <DialogActions><Button onClick={() => setSelectedRow(null)}>Vazgeç</Button><Button color="secondary" startIcon={<BlockIcon />} onClick={() => confirm(2)}>Reddet</Button><Button color="primary" startIcon={<CheckCircleOutlineIcon />} onClick={() => confirm(1)}>Onayla</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)}><Alert variant={notice?.type || 'default'} message={notice?.message || ''} onClose={() => setNotice(null)} /></Snackbar>
    </Page>
  );
};

export default TseHybSurecleriView;
